/**
 * Events service
 * Provides typed functions for interacting with the events table
 */

import { supabase } from '../supabase';
import type {
  Event,
  EventInsert,
  EventUpdate,
  EventSeriesInsert,
} from '../types/database';
import {
  generateRecurrenceDates,
  generateRRuleString,
} from '../utils/recurrence.utils';
import type { RecurrenceType } from '../types/events.types';

const PAGE_SIZE = 20;

/**
 * Paginated response type for infinite queries
 */
export type PaginatedEventsResponse = {
  data: Event[];
  nextCursor: number | undefined;
};

/**
 * Fetch all events
 */
export async function getEvents() {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .order('start_time', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch events: ${error.message}`);
  }

  return data as Event[];
}

/**
 * Fetch a single event by ID
 */
export async function getEventById(id: number) {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Failed to fetch event: ${error.message}`);
  }

  return data as Event;
}

/**
 * Fetch upcoming events
 */
export async function getUpcomingEvents() {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .gte('start_time', new Date().toISOString())
    .eq('is_cancelled', false)
    .order('start_time', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch upcoming events: ${error.message}`);
  }

  return data as Event[];
}

/**
 * Fetch past events
 */
export async function getPastEvents() {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .lt('start_time', new Date().toISOString())
    .eq('is_cancelled', false)
    .order('start_time', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch past events: ${error.message}`);
  }

  return data as Event[];
}

/**
 * Fetch upcoming events with pagination
 */
export async function getUpcomingEventsPaginated(
  pageParam: number = 0
): Promise<PaginatedEventsResponse> {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .gte('start_time', new Date().toISOString())
    .eq('is_cancelled', false)
    .order('start_time', { ascending: true })
    .range(pageParam, pageParam + PAGE_SIZE - 1);

  if (error) {
    throw new Error(`Failed to fetch upcoming events: ${error.message}`);
  }

  return {
    data: (data as Event[]) || [],
    nextCursor:
      data && data.length === PAGE_SIZE ? pageParam + PAGE_SIZE : undefined,
  };
}

/**
 * Fetch past events with pagination
 */
export async function getPastEventsPaginated(
  pageParam: number = 0
): Promise<PaginatedEventsResponse> {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .lt('start_time', new Date().toISOString())
    .eq('is_cancelled', false)
    .order('start_time', { ascending: false })
    .range(pageParam, pageParam + PAGE_SIZE - 1);

  if (error) {
    throw new Error(`Failed to fetch past events: ${error.message}`);
  }

  return {
    data: (data as Event[]) || [],
    nextCursor:
      data && data.length === PAGE_SIZE ? pageParam + PAGE_SIZE : undefined,
  };
}

/**
 * Fetch upcoming events the user has RSVP'd "yes" to
 */
export async function getGoingEvents(userId: string) {
  // First, get event IDs where user has RSVP'd "yes"
  const { data: rsvps, error: rsvpError } = await supabase
    .from('event_rsvps')
    .select('event_id')
    .eq('user_id', userId)
    .eq('status', 'yes');

  if (rsvpError) {
    throw new Error(`Failed to fetch RSVPs: ${rsvpError.message}`);
  }

  // If no RSVPs, return empty array
  if (!rsvps || rsvps.length === 0) {
    return [] as Event[];
  }

  // Extract event IDs
  const eventIds = rsvps
    .map((rsvp) => rsvp.event_id)
    .filter((id) => id !== null);

  // If no valid event IDs, return empty array
  if (eventIds.length === 0) {
    return [] as Event[];
  }

  // Fetch events that match those IDs, are upcoming, and not cancelled
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .in('id', eventIds)
    .gte('start_time', new Date().toISOString())
    .eq('is_cancelled', false)
    .order('start_time', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch going events: ${error.message}`);
  }

  return data as Event[];
}

/**
 * Fetch upcoming events the user has RSVP'd "yes" to with pagination
 */
export async function getGoingEventsPaginated(
  userId: string,
  pageParam: number = 0
): Promise<PaginatedEventsResponse> {
  // First, get event IDs where user has RSVP'd "yes"
  const { data: rsvps, error: rsvpError } = await supabase
    .from('event_rsvps')
    .select('event_id')
    .eq('user_id', userId)
    .eq('status', 'yes');

  if (rsvpError) {
    throw new Error(`Failed to fetch RSVPs: ${rsvpError.message}`);
  }

  // If no RSVPs, return empty array
  if (!rsvps || rsvps.length === 0) {
    return {
      data: [],
      nextCursor: undefined,
    };
  }

  // Extract event IDs
  const eventIds = rsvps
    .map((rsvp) => rsvp.event_id)
    .filter((id) => id !== null);

  // If no valid event IDs, return empty array
  if (eventIds.length === 0) {
    return {
      data: [],
      nextCursor: undefined,
    };
  }

  // Fetch events that match those IDs, are upcoming, and not cancelled
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .in('id', eventIds)
    .gte('start_time', new Date().toISOString())
    .eq('is_cancelled', false)
    .order('start_time', { ascending: true })
    .range(pageParam, pageParam + PAGE_SIZE - 1);

  if (error) {
    throw new Error(`Failed to fetch going events: ${error.message}`);
  }

  return {
    data: (data as Event[]) || [],
    nextCursor:
      data && data.length === PAGE_SIZE ? pageParam + PAGE_SIZE : undefined,
  };
}

/**
 * Search events by title and description
 * Returns both past and upcoming events, sorted by most recent first
 */
export async function searchEvents(query: string) {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('is_cancelled', false)
    .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
    .order('start_time', { ascending: false })
    .limit(20);

  if (error) {
    throw new Error(`Failed to search events: ${error.message}`);
  }

  return data as Event[];
}

/**
 * Create a new event
 */
export async function createEvent(event: EventInsert) {
  const { data, error } = await supabase
    .from('events')
    .insert(event)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create event: ${error.message}`);
  }

  return data as Event;
}

/**
 * Update an existing event
 */
export async function updateEvent(id: number, updates: EventUpdate) {
  const { data, error } = await supabase
    .from('events')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update event: ${error.message}`);
  }

  return data as Event;
}

/**
 * Delete an event
 */
export async function deleteEvent(id: number) {
  const { error } = await supabase.from('events').delete().eq('id', id);

  if (error) {
    throw new Error(`Failed to delete event: ${error.message}`);
  }

  return id;
}

/**
 * Create a series of recurring events
 * Generates all occurrences using RRULE and inserts them as individual events
 */
export async function createEventSeries(
  baseEvent: EventInsert,
  recurrenceType: RecurrenceType,
  recurrenceCount: number,
  createdBy: string
): Promise<Event[]> {
  // 1. Generate all occurrence dates using RRULE
  const startDate = new Date(baseEvent.start_time);
  const dates = generateRecurrenceDates(
    startDate,
    recurrenceType,
    recurrenceCount
  );

  // 2. Create series record
  const seriesData: EventSeriesInsert = {
    title: baseEvent.title,
    recurrence_rule: generateRRuleString(recurrenceType, recurrenceCount),
    created_by: createdBy,
  };

  const { data: series, error: seriesError } = await supabase
    .from('event_series')
    .insert(seriesData)
    .select()
    .single();

  if (seriesError) {
    throw new Error(`Failed to create event series: ${seriesError.message}`);
  }

  // 3. Calculate event duration
  const startTime = new Date(baseEvent.start_time);
  const endTime = new Date(baseEvent.end_time);
  const durationMs = endTime.getTime() - startTime.getTime();

  // 4. Create event for each date
  const events: EventInsert[] = dates.map((date) => {
    const eventEnd = new Date(date.getTime() + durationMs);

    return {
      ...baseEvent,
      start_time: date.toISOString(),
      end_time: eventEnd.toISOString(),
      series_id: series.id,
    };
  });

  const { data, error } = await supabase.from('events').insert(events).select();

  if (error) {
    throw new Error(`Failed to create events: ${error.message}`);
  }

  return data as Event[];
}

/**
 * Get all events in a series by series_id
 */
export async function getEventsBySeries(seriesId: string): Promise<Event[]> {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('series_id', seriesId)
    .order('start_time', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch series events: ${error.message}`);
  }

  return data as Event[];
}

/**
 * Fetch all events for export matching date filter (Admin only)
 */
export async function getAllEventsForExport(options?: {
  dateFrom?: string;
  dateTo?: string;
}): Promise<Event[]> {
  const { dateFrom, dateTo } = options || {};

  let query = supabase
    .from('events')
    .select('*')
    .order('start_time', { ascending: false });

  if (dateFrom) {
    query = query.gte('start_time', dateFrom);
  }

  if (dateTo) {
    query = query.lte('start_time', dateTo);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Failed to fetch events for export: ${error.message}`);
  }

  return (data as Event[]) || [];
}

